import React from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type { CoverRedoRun } from 'fym-shared/src/music24/coverRedo';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { CoverRedoPair } from './CoverRedoPair';
import { coverRedoMessages as messages } from './coverRedoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the redo banners; each part uses its own slice of it */
import styles from './CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The test ids of a redo banner: `cover-redo-working`, `date-redo-done`, ... */
export type RedoTestIdPrefix = 'cover-redo' | 'date-redo';

interface RedoWorkingProps {
  run: CoverRedoRun;
  /** The open posts that move to the new files. */
  postsCount: number;
  /** The largest `etaSec` of the kit assets that are being made now, or null. */
  etaSec?: number | null;
  /** "Redoing with the new cover · {ready} of {total} ready". */
  title: MessageDescriptor;
  testIdPrefix: RedoTestIdPrefix;
}

/**
 * A redo that runs (design kit-redo-cover c1): ready of total, the bar, and the time left. The cover
 * redo and the date redo show the same card with their own title.
 */
const RedoWorkingComponent: React.FC<RedoWorkingProps> = ({ run, postsCount, etaSec, title, testIdPrefix }) => {
  const intl = useIntl();
  const readyShare = run.total === 0 ? 0 : (run.ready / run.total) * 100;
  return (
    <section
      className={clsx(styles.banner, styles.bannerWork)}
      data-testid={`${testIdPrefix}-working`}
      aria-live="polite"
    >
      <div className={styles.body}>
        <h3 className={styles.title}>{intl.formatMessage(title, { ready: run.ready, total: run.total })}</h3>
        <div
          className={styles.progressBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={run.progress}
        >
          <i className={styles.barOk} style={{ width: `${readyShare}%` }} />
          <i style={{ width: `${Math.max(0, run.progress - readyShare)}%` }} />
        </div>
        <p className={styles.sub}>
          {postsCount > 0
            ? intl.formatMessage(messages.progressBodyPosts, { count: postsCount })
            : intl.formatMessage(messages.progressBody)}
        </p>
      </div>
      {etaSec !== undefined && etaSec !== null && etaSec > 0 && (
        <span className={styles.figure}>
          {intl.formatMessage(messages.progressEta, { minutes: Math.max(1, Math.round(etaSec / 60)) })}
        </span>
      )}
    </section>
  );
};

export const RedoWorking = TypedMemo(RedoWorkingComponent);

interface RedoEndProps {
  run: CoverRedoRun;
  failed: boolean;
  /** The new cover for the done card, or null (the date redo shows no picture). */
  currentUrl: string | null;
  /** "Your kit has the new cover." */
  doneTitle: MessageDescriptor;
  /** "{ready} of {total} have the new cover." */
  failedBody: MessageDescriptor;
  /** True when files are still out of date: the failed card offers "Try again". */
  canRetry: boolean;
  onClose: () => void;
  onRetry: () => void;
  testIdPrefix: RedoTestIdPrefix;
}

/** The end of a redo run (design kit-redo-cover c2, c3): done, or the files that failed. */
const RedoEndComponent: React.FC<RedoEndProps> = ({
  run,
  failed,
  currentUrl,
  doneTitle,
  failedBody,
  canRetry,
  onClose,
  onRetry,
  testIdPrefix,
}) => {
  const intl = useIntl();
  const close = (
    <button type="button" className={styles.close} onClick={onClose} aria-label={intl.formatMessage(messages.close)}>
      ×
    </button>
  );
  if (!failed) {
    return (
      <section className={clsx(styles.banner, styles.bannerDone)} data-testid={`${testIdPrefix}-done`}>
        {currentUrl !== null && <CoverRedoPair previousUrl={null} currentUrl={currentUrl} small />}
        <div className={styles.body}>
          <h3 className={styles.title}>{intl.formatMessage(doneTitle)}</h3>
          <p className={styles.sub}>
            {intl.formatMessage(messages.doneBody, { count: run.ready })}
            {run.postsSwapped > 0 && ` ${intl.formatMessage(messages.donePosts, { count: run.postsSwapped })}`}
          </p>
        </div>
        {close}
      </section>
    );
  }
  return (
    <section className={clsx(styles.banner, styles.bannerBad)} data-testid={`${testIdPrefix}-failed`}>
      <div className={styles.body}>
        <h3 className={styles.title}>{intl.formatMessage(messages.failedTitle, { count: run.failed })}</h3>
        <p className={styles.sub}>
          {intl.formatMessage(failedBody, { ready: run.ready, total: run.total })}
          {run.failedCredits > 0 &&
            ` ${intl.formatMessage(messages.failedCreditsBack, { credits: run.failedCredits })}`}
        </p>
      </div>
      <div className={styles.buttons}>
        {close}
        {canRetry && (
          <SMLButton variant="primary" onClick={onRetry}>
            {intl.formatMessage(messages.tryAgain)}
          </SMLButton>
        )}
      </div>
    </section>
  );
};

export const RedoEnd = TypedMemo(RedoEndComponent);
